import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { t, tDyn } from '../i18n';
import { clearOfflineData } from '../offline';

/** Beitritt über einen Einladungslink – mit neuem Konto, bestehendem Konto oder angemeldet. */
export function InvitePage() {
  const { token = '' } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const auth = useQuery({ queryKey: ['auth'], queryFn: api.authState, staleTime: Infinity });
  // Nicht automatisch neu laden: nach dem Beitritt ist ein Einmal-Link verbraucht
  const info = useQuery({ queryKey: ['invite', token], queryFn: () => api.inviteInfo(token), retry: false, staleTime: Infinity });
  const [mode, setMode] = useState<'register' | 'login'>('register');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const user = auth.data?.user ?? null;

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const join = (account?: { name: string; password: string }) =>
    run(async () => {
      const { campaignId, user: joined } = await api.acceptInvite(token, account);
      // Erst weg von der Einladungsseite, dann aufräumen (sonst lädt sie den verbrauchten Link neu)
      qc.setQueryData(['auth'], { needsSetup: false, user: joined });
      navigate(`/c/${campaignId}`, { replace: true });
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
      window.setTimeout(() => qc.removeQueries({ queryKey: ['invite', token] }), 0);
    });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'register') {
      if (password !== repeat) return setError(t('account.mismatch'));
      void join({ name, password });
    } else {
      void run(async () => {
        await api.login(name, password);
        await qc.invalidateQueries({ queryKey: ['auth'] });
        await info.refetch();
      });
    }
  };

  const switchUser = () =>
    run(async () => {
      await api.logout();
      await clearOfflineData();
      await qc.invalidateQueries({ queryKey: ['auth'] });
      await info.refetch();
    });

  const head = (
    <h1>
      <span className="logo">▤</span> {t('app.name')}
    </h1>
  );

  if (auth.isLoading || info.isLoading) return <div className="center-screen muted">{t('common.loading')}</div>;
  if (info.isError) {
    return (
      <div className="center-screen">
        <div className="card auth">
          {head}
          <p className="error">{t('invite.invalid')}</p>
        </div>
      </div>
    );
  }
  const inv = info.data!;

  return (
    <div className="center-screen">
      <form className="card auth" onSubmit={submit}>
        {head}
        <h2>{t('invite.title')}</h2>
        <p>{t('invite.intro', { name: inv.campaignName, role: tDyn(`role.${inv.role}`) })}</p>
        {user ? (
          <>
            {inv.alreadyMember && <p className="muted">{t('invite.already')}</p>}
            <button type="button" className="primary" disabled={busy} onClick={() => void join()}>
              {inv.alreadyMember ? t('invite.open') : t('invite.joinAs', { name: user.name })}
            </button>
            <button type="button" className="link-button" disabled={busy} onClick={() => void switchUser()}>
              {t('invite.switchUser')}
            </button>
          </>
        ) : (
          <>
            <p className="muted small">{mode === 'register' ? t('invite.register') : t('invite.loginFirst')}</p>
            <label>
              {t('common.name')}
              <input autoFocus autoComplete="username" value={name} onChange={(e) => setName(e.target.value)} required />
            </label>
            <label>
              {t('common.password')}
              <input
                type="password"
                autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={mode === 'register' ? 10 : 1}
                required
              />
              {mode === 'register' && <span className="muted small">{t('auth.passwordHint')}</span>}
            </label>
            {mode === 'register' && (
              <label>
                {t('account.repeat')}
                <input type="password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} required />
              </label>
            )}
            <button className="primary" disabled={busy}>
              {mode === 'register' ? t('invite.join') : t('auth.login')}
            </button>
            <button
              type="button"
              className="link-button"
              onClick={() => {
                setMode(mode === 'register' ? 'login' : 'register');
                setError(null);
              }}
            >
              {mode === 'register' ? t('invite.haveAccount') : t('invite.newAccount')}
            </button>
          </>
        )}
        {error && <div className="error">{error}</div>}
      </form>
    </div>
  );
}
